import * as React from 'react';

import {
    ContextualMenu,
    ContextualMenuItemType,
    DirectionalHint,
    IContextualMenuItem,
} from '@fluentui/react/lib/ContextualMenu';
import {
    addPublicFolderContextMenuItemText,
    deleteCalendarGroupString,
    newGroupString,
    newTeamString,
    renameCalendarGroupString,
} from './CalendarPickerGroupContextMenu.locstring.json';
import { moveDownText, moveUpText } from './CalendarPickerCalendarContextMenu.locstring.json';

import type { CalendarGroup } from 'owa-graph-schema';
import type ContextMenuViewState from '../store/schema/ContextMenuViewState';
import { assertNever } from 'owa-assert';
import { getCalendarGroups } from 'owa-calendar-cache';
import { getUnifiedGroupsSettingsStore } from 'owa-groups-shared-store/lib/UnifiedGroupsSettingsStore';
import { isConnectedAccount } from 'owa-accounts-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import { newCalendarGroupString } from '../strings.locstring.json';
import { observer } from 'mobx-react-lite';
import { updateCalendarGroupDisplayOrder } from 'owa-calendar-update';
import { useComputed } from 'owa-react-hooks/lib/useComputed';

export interface CalendarPickerGroupContextMenuProps {
    group: CalendarGroup;
    contextMenuViewState: ContextMenuViewState | null;
    enableDeleteCalendarGroup: boolean;
    enableRenameCalendarGroup: boolean;
    enableAddPublicFolderToFavorites: boolean;
    hasMultipleAccounts: boolean;
    prevGroupId?: string;
    nextGroupId?: string;
    onDismiss: () => void;
    onNewGroup: () => void;
    onNewCalendarGroup: () => void;
    onRenameCalendarGroup: () => void;
    onDeleteCalendarGroup: () => void;
    onAddPublicFolderToFavorites: () => void;
    onNewTeamsCalendar: () => void;
}

export default observer(function CalendarPickerGroupContextMenu(
    props: CalendarPickerGroupContextMenuProps
) {
    const contextMenuItems = useComputed((): IContextualMenuItem[] => {
        const {
            group,
            onNewCalendarGroup,
            onRenameCalendarGroup,
            onDeleteCalendarGroup,
            onNewGroup,
            onAddPublicFolderToFavorites,
            enableRenameCalendarGroup,
            enableDeleteCalendarGroup,
            enableAddPublicFolderToFavorites,
            hasMultipleAccounts,
            onNewTeamsCalendar,
            prevGroupId,
            nextGroupId,
        } = props;

        switch (group.calendarGroupId.mailboxInfo.type) {
            case 'UserMailbox':
                return [
                    {
                        key: 'rename_calendar_group',
                        text: loc(renameCalendarGroupString),
                        onClick: onRenameCalendarGroup,
                        disabled: !enableRenameCalendarGroup,
                    },
                    {
                        key: 'delete_calendar_group',
                        text: loc(deleteCalendarGroupString),
                        onClick: onDeleteCalendarGroup,
                        disabled: !enableDeleteCalendarGroup,
                    },
                    {
                        key: 'divider_1',
                        text: '-',
                        itemType: ContextualMenuItemType.Divider,
                    },
                    ...(hasMultipleAccounts
                        ? []
                        : [
                              {
                                  key: 'new_calendar_group',
                                  text: loc(newCalendarGroupString),
                                  onClick: onNewCalendarGroup,
                              },
                          ]),
                    {
                        key: 'Add_Public_Folder_To_Favorites',
                        text: loc(addPublicFolderContextMenuItemText),
                        onClick: onAddPublicFolderToFavorites,
                        disabled: !enableAddPublicFolderToFavorites,
                    },
                    ...(isFeatureEnabled('cal-calendarPickerReorder')
                        ? [
                              {
                                  key: 'calendargroup_reorderDivider',
                                  itemType: ContextualMenuItemType.Divider,
                              },
                              {
                                  key: loc(moveUpText),
                                  text: loc(moveUpText),
                                  disabled: !prevGroupId,
                                  onClick: onGroupMoveUp,
                              },
                              {
                                  key: loc(moveDownText),
                                  text: loc(moveDownText),
                                  disabled: !nextGroupId,
                                  onClick: onGroupMoveDown,
                              },
                          ]
                        : []),
                ];
            case 'GroupMailbox':
                const enableGroupCreation = getUnifiedGroupsSettingsStore().groupCreationEnabled;
                return !isConnectedAccount(group.calendarGroupId.mailboxInfo.userIdentity)
                    ? [
                          {
                              key: 'new_group',
                              text: loc(newGroupString),
                              onClick: onNewGroup,
                              disabled: !enableGroupCreation,
                          },
                      ]
                    : [];
            case 'TeamsMailbox':
                return !isConnectedAccount(group.calendarGroupId.mailboxInfo.userIdentity) &&
                    isFeatureEnabled('cal-showAddTeamsCalendars')
                    ? [
                          {
                              key: 'new_team',
                              text: loc(newTeamString),
                              onClick: onNewTeamsCalendar,
                          },
                      ]
                    : [];
            case 'PublicMailbox':
            case 'SharedMailbox':
            case 'ArchiveMailbox':
                return null;
            default:
                return assertNever(group.calendarGroupId.mailboxInfo.type as never);
        }
    });
    const onGroupMoveUp = () => {
        const nextGroupId = props.prevGroupId;
        const prevGroupId = getPreviousOrNextGroup(nextGroupId, true /* shouldGetPrevious */);

        updateCalendarGroupDisplayOrder(
            props.group.calendarGroupId.mailboxInfo.userIdentity /* userIdentity */,
            props.group.serverGroupId /* draggedItemId */,
            prevGroupId /* prevItemId */,
            nextGroupId /* nextItemId */
        );
    };
    const onGroupMoveDown = () => {
        const prevGroupId = props.nextGroupId;
        const nextGroupId = getPreviousOrNextGroup(prevGroupId, false /* shouldGetPrevious */);

        updateCalendarGroupDisplayOrder(
            props.group.calendarGroupId.mailboxInfo.userIdentity /* userIdentity */,
            props.group.serverGroupId /* draggedItemId */,
            prevGroupId /* prevItemId */,
            nextGroupId /* nextItemId */
        );
    };
    const getPreviousOrNextGroup = (groupId: string, shouldGetPrevious: boolean) => {
        let resGroupId: string;
        const userIdentity = props.group.calendarGroupId.mailboxInfo.userIdentity;
        const calendarGroups: CalendarGroup[] = getCalendarGroups().filter(
            group =>
                group.calendarGroupId.mailboxInfo.type === 'UserMailbox' &&
                group.calendarGroupId.mailboxInfo.userIdentity == userIdentity
        );

        for (let i = 0; i < calendarGroups.length; i++) {
            if (
                calendarGroups[i].serverGroupId == groupId &&
                calendarGroups[i].calendarGroupId.mailboxInfo.userIdentity == userIdentity
            ) {
                const index = shouldGetPrevious ? i - 1 : i + 1;
                if (index >= 0 && index < calendarGroups.length) {
                    resGroupId = calendarGroups[index].serverGroupId;
                }
                break;
            }
        }

        return resGroupId;
    };
    const { contextMenuViewState, onDismiss } = props;
    return contextMenuViewState ? (
        <ContextualMenu
            items={contextMenuItems.get()}
            target={contextMenuViewState.anchorElement}
            directionalHint={DirectionalHint.bottomLeftEdge}
            onDismiss={onDismiss}
        />
    ) : null;
});
